import {WildMeterView} from 'src/wildmeter/WildMeterView';
import {Module} from '@fantasmagames/slot/src/common/Module';
import {GameViews} from 'src/GameViews';
import {ReelsEvents} from '@fantasmagames/slot/src/modules/reels/spin/ReelsEvents';
import {GameAvalancheReelsEvents} from 'src/avalanche/GameAvalancheReelsEvents';
import {GameRecodeLogic} from 'src/logic/recode/GameRecodeLogic';
import {ReelConstants, SymbolPosition} from '@fantasmagames/slot/src/modules/reels/ReelConstants';
import {WildMeterEvents} from 'src/wildmeter/WildMeterEvents';
import {FreeSpinsEvents} from '@fantasmagames/slot/src/modules/freespins/intro/FreeSpinsEvents';
import {WinPresentationEvents} from '@fantasmagames/slot/src/modules/winpresentation/WinPresentationEvents';
import {StartScreenEvents} from '@fantasmagames/slot/src/modules/startscreen/StartScreenEvents';
import {TimingConstants} from 'src/timing/TimingConstants';
import {GameBackgroundModule} from 'src/background/GameBackgroundModule';

export class WildMeterModule extends Module<WildMeterView> {
	protected enterFirstState(): void {
	}

	public restore(): void {
		const logic: GameRecodeLogic = this.logic as GameRecodeLogic;

		if (logic.isFreeSpinsStarted) {
			const initialWildMeterValue: number = logic.wildMeter - logic.getTotalWildMeterDeltas();
			const coinsOnMeter = initialWildMeterValue % 3;
			const currentMultiplierValue = 1 + (Math.floor(initialWildMeterValue / 3) * 2);
			this.view.isInFreeSpins = logic.isFreeSpinsStarted;
			this.view.currentMultiplierValue = currentMultiplierValue;
			this.view.setMultiplierValue(currentMultiplierValue);

			const animationIndex = Math.round(currentMultiplierValue / 2);
			const mpValue = currentMultiplierValue > 10 ? 10 : currentMultiplierValue;
			if (animationIndex >= WildMeterView.MP_LVL_TO_UPGRADE_IDLE[0]) {
				this.view.updateMultiplierIdleAnimation(mpValue);
			} else if (coinsOnMeter > 0) {
				this.view.playMultiplierIdleAnimation(1);
			}

			coinsOnMeter > 0 && this.view.updateCoinsOnMeter(coinsOnMeter);
		}
	}

	protected setupStates(): void {

		this.on(ReelsEvents.REELS_SPINNING_STARTED, this.onSpinStarted);
		this.on(GameAvalancheReelsEvents.WIN_SYMBOLS_EXPLOSION_STARTED, this.onWinExplosionStarted);

		this.on(FreeSpinsEvents.FREE_SPINS_INTRO_STARTED, this.onFreeSpinsIntroStarted);
		this.on(FreeSpinsEvents.FREE_SPINS_OUTRO_CONTINUE_CLICKED, this.onFreeSpinsOutroContinued);

		this.on(WinPresentationEvents.WIN_NUMBER_BOUNCE_FINISHED, this.onWinBounceFinished);

		this.onViewEmitEvent(WildMeterView.WIN_METER_WILDS_ANIMATIONS_FINISHED, WildMeterEvents.WIN_METER_WILDS_ANIMATIONS_FINISHED);
		this.onViewEmitEvent(WildMeterView.WIN_METER_EXIT_WIN_ANIMATION_STARTED, WildMeterEvents.WIN_METER_EXIT_WIN_ANIMATION_STARTED);
		this.onViewEmitEvent(WildMeterView.WIN_METER_EXIT_WIN_ANIMATION_FINISHED, WildMeterEvents.WIN_METER_EXIT_WIN_ANIMATION_FINISHED);
		this.onViewEmitEvent(WildMeterView.ADD_WILD_COIN_TO_REELS, WildMeterEvents.ADD_WILD_COIN_TO_REELS);
		this.onViewEmitEvent(WildMeterView.SEND_COIN_TO_METER, WildMeterEvents.SEND_COIN_TO_METER);
		this.onViewEmitEvent(WildMeterView.COIN_HIT_METER, WildMeterEvents.COIN_HIT_METER);
		this.onViewEmitEvent(WildMeterView.MULTIPLIER_UDPATE, WildMeterEvents.MULTIPLIER_UDPATE);
		this.onViewEmitEvent(WildMeterView.INDICATOR_LIT, WildMeterEvents.INDICATOR_LIT);

		this.on(StartScreenEvents.INTRO_STARTED, this.onStartScreenIntroStarted);
		this.on(GameBackgroundModule.TRANSITION_ANIMATION_STARTED, this.onTransitionAnimationStarted);

		this.on(FreeSpinsEvents.FREE_SPINS_INTRO_STARTED, this.onFreeSpinsIntroStarted);
		this.on(FreeSpinsEvents.FREE_SPINS_RESTORE_INTRO_STARTED, this.onFreeSpinsIntroStarted);
		this.on(FreeSpinsEvents.FREE_SPINS_OUTRO_STARTED, this.onFreeSpinsOutroStarted);
		this.on(FreeSpinsEvents.FREE_SPINS_INTRO_FINISHED, this.onFreeSpinsIntroFinished);
		this.on(FreeSpinsEvents.FREE_SPINS_OUTRO_FINISHED, this.onFreeSpinsOutroFinished);
	}

	protected onFreeSpinsOutroStarted(): void {
		this.view.hide();
	}

	protected onFreeSpinsIntroFinished(): void {
		this.view.show();
	}

	protected onFreeSpinsOutroFinished(): void {
		this.view.show();
	}

	protected onStartScreenIntroStarted(): void {
		this.view.setReelsContainerAlphaToZero();
	}

	protected onTransitionAnimationStarted(): void {
		this.view.playReelContainerTransition();
	}

	protected currentRoundMultiplier(): number {
		const logic: GameRecodeLogic = this.logic as GameRecodeLogic;

		if (logic.wildMeterDeltas.length) {
			const currentRoundMultiplier: number = Math.floor(logic.wildMeterDeltas.reduce((a, b) => a + b) / 3);

			return currentRoundMultiplier * 2;
		} else {
			return 0;
		}
	}

	protected getFreeSpinsTotalMultiplier(wildMeter: number): number {
		const defaultMultiplier: number = 1;
		const multiplier = Math.floor(wildMeter / 3);

		return multiplier ? multiplier * 2 + defaultMultiplier : 1;
	}

	protected setupViews(): void {
		this.setView(GameViews.WILD_METER_VIEW);
	}

	protected onWinBounceFinished(): void {
		const logic: GameRecodeLogic = this.logic as GameRecodeLogic;

		if (logic.getLastAvalancheMultiplier() > 1) {
			this.view.playWinAimation();
			this.view.setMultiplierValue(logic.getLastAvalancheMultiplier(), false, true);
			this.view.setMultiplierValue(logic.getLastAvalancheMultiplier(), true, true);
		}
	}

	protected onFreeSpinsIntroStarted(): void {
		this.view.resetCoins();
		this.view.restoreMultiplier();
		this.view.isInFreeSpins = true;
		this.view.hide();
	}

	protected onFreeSpinsOutroContinued(): void {
		this.view.isInFreeSpins = false;
	}

	protected onSpinStarted(): void {
		const logic: GameRecodeLogic = this.logic as GameRecodeLogic;

		if (!logic.isFreeSpinsStarted) {
			this.view.fullMultiplierRestore();
		}
	}

	protected onWinExplosionStarted(): void {
		const logic: GameRecodeLogic = this.logic as GameRecodeLogic;

		const wildWinPositions: SymbolPosition[] = logic.getCurrentRoundWinningWilds();

		if (wildWinPositions.length === 0) {
			return;
		}

		let delay: number = 0;

		for (let reelIndex = 0; reelIndex < ReelConstants.REELS_AMOUNT; reelIndex++) {
			const symbolsOnReel: SymbolPosition[] = this.getAllSymbolPositionsFromReel(reelIndex, wildWinPositions);

			if (symbolsOnReel.length) {
				this.timeUtils.createTimer(() => {
					for (const symPos of symbolsOnReel) {
						this.view.addWildCoinOntoReels(symPos);
					}
				}, delay);
				delay += TimingConstants.SYMBOL_REMOVE_STAGGER_DELAY;
			}
		}

		delay += TimingConstants.START_SEND_COINS_DELAY;

		this.timeUtils.createTimer(() => {
			this.view.startSendingCoinsToWildMeter();
		}, delay);
	}

	// MHNOTE: Blunt copy from AvalancheReelsView. Should ideally be put in a helper class.
	protected getAllSymbolPositionsFromReel(reelIndex: number, symbols: SymbolPosition[]): SymbolPosition[] {
		const syms: SymbolPosition[] = [];
		for (const sym of symbols) {
			if (sym.x === reelIndex) {
				syms.push(sym);
			}
		}

		return syms;
	}
}



// WEBPACK FOOTER //
// ./src/wildmeter/WildMeterModule.ts